import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    CallBack,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    QueryParams,
} from "../models";
import { Operation } from "rfc6902";

export interface IUseCallBackQuery extends IUseQueryPaged<CallBack> {}

interface UseCallBackQueryParams extends QueryHookParams {
    caseId?: number | string;
}

export const useCallBackQuery = (params?: UseCallBackQueryParams): Omit<IUseCallBackQuery, "deleteMutation"> => {
    const callBackId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.CallBacks);
    const url = Endpoints.CallBacks;
    const queryType = getQueryType(callBackId, params?.queryType);
    const queryParamsWithCaseId: QueryParams = {
        ...queryParams,
        caseId: params?.caseId ? `${params.caseId}` : "",
    };

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<CallBack>>(getRequestOptions(url, config, queryParamsWithCaseId, sortParams, pageParams)),
        queryKey: queryKeys.callBacks.pagedList({ ...queryParamsWithCaseId, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<CallBack>>(
                getRequestOptions(url, config, queryParamsWithCaseId, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.callBacks.list({ ...queryParamsWithCaseId, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<CallBack>({ url: `${url}/${callBackId}`, config }),
        queryKey: queryKeys.callBacks.detail(callBackId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<CallBack>) => post<Partial<CallBack>, CallBack>({ url, data }),
        queryKey: queryKeys.callBacks.all(),
        keysToInvalidate: [queryKeys.callBacks.lists(), queryKeys.callBacks.pagedLists()],
        successToast: "Call Back created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], CallBack>({ url: `${url}/${callBackId}`, data }),
        queryKey: queryKeys.callBacks.detail(callBackId),
        keysToInvalidate: [queryKeys.callBacks.lists(), queryKeys.callBacks.pagedLists()],
        successToast: "Call Back updated",
    });

    return useMemo<Omit<IUseCallBackQuery, "deleteMutation">>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation]
    );
};
